import React, { Dispatch, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ExtraHeaderContent, Header } from 'adminDashboard/benefitDetails/common/ProductClassSummaryComponents'
import { Card, Col, Collapse, Row, Skeleton } from 'antd'
//
import global, { formatMoney } from 'common'

import getProductClassDesignSummary, {
  ProductClassDesignSummaryResponse,
} from 'core/application/adminDashboard/benefitDetails/design/productClass/getProductClassDesignSummary'
import { BenefitProductClassResponse } from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'
import { FamilyStatus } from 'core/domain/companies/familyStatus'

import Icon from 'common/components/general/Icon'

import { parseZoneMonthYearFormat } from 'common/dateFormatters'

import QsehraBenefitDesignLogo from 'assets/images/Qsehra-benefit-design-icon 1.svg'

//
import './product-class-details.less'

type ProductClassProps = {
  item: BenefitProductClassResponse
  collapseKey: number
  benefitStartDate?: any
  employeeEligibilityType?: any
  onEdit?: () => any
  update?: boolean
  setUpdate?: Dispatch<boolean>
  onDelete?: () => any
  colSpan?: number
  canEdit?: boolean
}

const ProductClass = ({
  item,
  collapseKey,
  benefitStartDate,
  employeeEligibilityType,
  onEdit,
  update = false,
  setUpdate = () => undefined,
  onDelete,
  colSpan = 14,
  canEdit,
}: ProductClassProps) => {
  const { t: translate } = useTranslation()
  const [productClasses, areProductClassesLoading, , reloadProductClassDetails] =
    global.hooks.useQueryRequest<ProductClassDesignSummaryResponse>(null, () => getProductClassDesignSummary(item.id))
  /**
   * update is a flag sent by parent to trigger details update from back-end,
   * the component after update finished sets the flag again to false
   */
  useEffect(() => {
    if (update) {
      reloadProductClassDetails().finally(() => {
        setUpdate(false)
      })
    }
  }, [update])

  return item.designCompleted ? (
    <Row className={'mb-8 t-product-class-details'}>
      <Col span={colSpan} className="pk-qsehra__design-classes">
        <Collapse className="design_classes_collapse">
          <Collapse.Panel
            className="class_design"
            key={collapseKey}
            header={
              <>
                <Row align={'middle'} justify={'space-between'} className={'w-100'}>
                  <Col span={24}>
                    <Header title={item.name} />
                    <div className={'color-gray-7'}>
                      <global.components.general.Icon className="fal fa-calendar-star mr-4 " />
                      {translate('admin-dashboard.benefit-details.qsehra-benefit-design.benefit-start-date')}
                      <span className={'t-benefit-start-date'}>{parseZoneMonthYearFormat(benefitStartDate)}</span>
                    </div>
                  </Col>
                </Row>
              </>
            }
            extra={
              <ExtraHeaderContent
                content={
                  <div>
                    {canEdit && (
                      <global.components.general.Button className="mr-16" onClick={onEdit}>
                        {translate('admin-dashboard.benefit-details.summary.edit-button')}
                      </global.components.general.Button>
                    )}

                    {onDelete && item.totalEmployees === 0 && (
                      <global.components.general.Icon className="fal fa-trash-alt" onClick={onDelete} />
                    )}
                  </div>
                }
              />
            }
          >
            <Skeleton loading={areProductClassesLoading}>
              {productClasses && (
                <SubCollapse
                  premiumName={productClasses.reimbursementProduct.name}
                  reimburseAnotherEmployee={productClasses?.reimburseAnotherEmployeePlan}
                  familyAllowances={productClasses?.familyAllowances}
                  nextFamilyAllowances={productClasses?.nextFamilyAllowances}
                  offerSameAllowance={productClasses?.offerSameAllowance}
                />
              )}
              {productClasses && (
                <p className="mt-16 mb-0 gray-7">
                  <global.components.general.Icon className="fal fa-clock mr-8" />
                  {translate('admin-dashboard.benefit-details.summary.waiting-period-label')}
                  <strong className="class-summary__subitem ml-4 t-waiting-period">
                    {productClasses?.waitingPeriod !== 0
                      ? `${productClasses?.waitingPeriod} ${translate(
                          'admin-dashboard.benefit-details.summary.waiting-period-value-label',
                        )}`
                      : `${translate('admin-dashboard.benefit-details.summary.no-waiting-period-value-label')}`}
                  </strong>
                </p>
              )}
            </Skeleton>
          </Collapse.Panel>
        </Collapse>
      </Col>
    </Row>
  ) : (
    <>
      <Row className={'mb-8'}>
        <Col span={17}>
          <Card>
            <Row>
              <Col span={16}>
                <h5>
                  {employeeEligibilityType === 0
                    ? translate('admin-dashboard.benefit-details.qsehra-benefit-design.full-time')
                    : translate('admin-dashboard.benefit-details.qsehra-benefit-design.full-time-part-time')}
                </h5>
                <Row className={'mb-32 color-gray-7'}>
                  <Col span={18}>
                    <global.components.general.Icon className="fal fa-calendar-star mr-4 " />
                    {translate('admin-dashboard.benefit-details.qsehra-benefit-design.benefit-start-date')}
                    {parseZoneMonthYearFormat(benefitStartDate)}
                  </Col>
                </Row>
                <global.components.general.Button type={'primary'} ghost onClick={onEdit}>
                  {translate('admin-dashboard.benefit-details.qsehra-benefit-design.button-title')}
                </global.components.general.Button>
              </Col>
              <Col span={8} className={'text-right'}>
                <img src={QsehraBenefitDesignLogo} alt={'logo'} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ProductClass

type SubCollapseTypesProps = {
  premiumName: string
  reimburseAnotherEmployee: boolean
  familyAllowances: any[]
  nextFamilyAllowances: any[]
  offerSameAllowance: boolean
}

const SubCollapse = ({
  premiumName,
  reimburseAnotherEmployee,
  familyAllowances,
  nextFamilyAllowances,
  offerSameAllowance,
}: SubCollapseTypesProps) => {
  const { t: translate } = useTranslation()
  const icons = [
    <Icon className="fal fa-user  ml-4 mr-8" />,
    <Icon className="fal fa-user-friends mr-8" />,
    <Icon className="fal fa-user-friends mr-8" />,
    <Icon className="fal fa-users mr-8" />,
  ]
  return (
    <>
      <Row className="design_classes_collapse_inner mb-16">
        <Col span={24}>
          <strong className="class-summary__subtitle gray-7 mb-8">
            {translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.expense-type')}
          </strong>
        </Col>
        <Col span={24}>
          <p className="class-summary__item gray-8 t-expense-type">{premiumName}</p>
        </Col>
      </Row>
      <Row className="design_classes_collapse_inner mb-16">
        <Col span={24}>
          <strong className="class-summary__subtitle gray-7 mb-8">
            {translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.reimburse-another-employee-plan')}
          </strong>
        </Col>
        <Col span={24}>
          <p className="class-summary__item gray-8 t-reimburse-another-employee">
            {reimburseAnotherEmployee
              ? translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.yes')
              : translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.no')}
          </p>
        </Col>
      </Row>
      <Row className="design_classes_collapse_inner mb-16">
        <Col span={24}>
          <p className="class-summary__subtitle gray-7 mb-8">
            {translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.monthly-allowance')}
          </p>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={24}>
              <p className="class-summary__item gray-8 mb-8 t-offer-same-allowance">
                {offerSameAllowance
                  ? translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.same-allowance')
                  : translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.different-allowance')}
              </p>
            </Col>
          </Row>

          <Row>
            <Col span={18}>
              <strong className="class-summary__item gray-8 mb-8">
                <span>{icons[0]}&nbsp;&nbsp; Single</span>
                {nextFamilyAllowances.length > 1 && (
                  <span className={'t-next-single-family-allowance'}>
                    {' '}
                    <i>
                      (Starting from next month the allowance will be: $
                      {formatMoney(nextFamilyAllowances.find((x) => x.familyId == FamilyStatus.Single)?.amount)})
                    </i>
                  </span>
                )}
                <br />
              </strong>
              <strong className="class-summary__item gray-8 mb-8">
                <span>{icons[1]}&nbsp;&nbsp; Single with dependents</span>
                {nextFamilyAllowances.length > 1 && (
                  <span className={'t-next-single-dependents-family-allowance'}>
                    {' '}
                    <i>
                      (Starting from next month the allowance will be: $
                      {formatMoney(
                        nextFamilyAllowances.find((x) => x.familyId == FamilyStatus.SingleWithDependents)?.amount,
                      )}
                      )
                    </i>
                  </span>
                )}
                <br />
              </strong>
              <strong className="class-summary__item gray-8 mb-8">
                <span>{icons[2]}&nbsp;&nbsp; Married</span>
                {nextFamilyAllowances.length > 1 && (
                  <span className={'t-next-married-family-allowance'}>
                    {' '}
                    <i>
                      (Starting from next month the allowance will be: $
                      {formatMoney(nextFamilyAllowances.find((x) => x.familyId == FamilyStatus.Married)?.amount)})
                    </i>
                  </span>
                )}
                <br />
              </strong>
              <strong className="class-summary__item gray-8 mb-8">
                <span>{icons[3]}&nbsp;&nbsp; Married with dependents</span>
                {nextFamilyAllowances.length > 1 && (
                  <span className={'t-next-married-dependents-family-allowance'}>
                    {' '}
                    <i>
                      (Starting from next month the allowance will be: $
                      {formatMoney(
                        nextFamilyAllowances.find((x) => x.familyId == FamilyStatus.MarriedWithDependents)?.amount,
                      )}
                      )
                    </i>
                  </span>
                )}
                <br />
              </strong>
            </Col>
            <Col span={6} className="text-right">
              <strong className=" class-summary__subitem gray-7 t-single-family-allowance">
                {formatMoney(familyAllowances.find((x) => x.familyId == FamilyStatus.Single)?.amount)}
                <br />
              </strong>
              <strong className=" class-summary__subitem gray-7 t-single-dependents-family-allowance">
                {formatMoney(familyAllowances.find((x) => x.familyId == FamilyStatus.SingleWithDependents)?.amount)}
                <br />
              </strong>
              <strong className=" class-summary__subitem gray-7 t-married-family-allowance">
                {formatMoney(familyAllowances.find((x) => x.familyId == FamilyStatus.Married)?.amount)}
                <br />
              </strong>
              <strong className=" class-summary__subitem gray-7 t-married-dependents-family-allowance">
                {formatMoney(familyAllowances.find((x) => x.familyId == FamilyStatus.MarriedWithDependents)?.amount)}
                <br />
              </strong>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}
